import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { apiErrorMessage } from '../../../hooks/useErrorHandler'
import { usePermissions } from '../../../hooks/useAuth'
import api from '../../../lib/api'
import { formatCurrencyWithCentsOrDash } from '../../../lib/format'
import { describeProposedRates, pricingModelLabel } from '../../../lib/statementAnalysis'

interface LeadAnalysis {
  id: string
  merchant_label: string
  statement_period: string | null
  pricing_model: string
  proposed_rates: Record<string, string> | null
  result: { merchant_savings?: string | null; agms_margin?: string | null } | null
}

interface LeadAnalysisList {
  selected_statement_analysis_id: string | null
  data: LeadAnalysis[]
}

interface LeadStatementAnalysesProps {
  leadId: string
  canUpdateLead: boolean
}

export function LeadStatementAnalyses({ leadId, canUpdateLead }: LeadStatementAnalysesProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const permissions = usePermissions()
  const canUse = permissions?.canUseStatementAnalysis?.() ?? false

  const analyses = useQuery({
    queryKey: ['lead-statement-analyses', leadId],
    enabled: canUse && leadId !== '',
    queryFn: async () => (await api.get<LeadAnalysisList>(`/leads/${leadId}/statement-analyses`)).data,
  })

  const start = useMutation({
    mutationFn: async () => (await api.post<{ id: string }>(`/leads/${leadId}/statement-analyses`, {})).data,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['lead-statement-analyses', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
      navigate(`/statement-analysis/${created.id}`)
    },
  })

  const select = useMutation({
    mutationFn: async (statementAnalysisId: string) => (
      await api.put(`/leads/${leadId}/selected-statement-analysis`, {
        statement_analysis_id: statementAnalysisId,
      })
    ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-statement-analyses', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
    },
  })

  if (!canUse) {
    return null
  }

  const selectedId = analyses.data?.selected_statement_analysis_id ?? null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Statement analyses</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            Savings and proposed rates from analyses linked to this lead. The selected analysis is copied into TurboApp pricing, where it can still be edited.
          </p>
          <Button type="button" onClick={() => start.mutate()} disabled={start.isPending}>
            Start analysis
          </Button>
        </div>
        {start.isError ? (
          <p className="text-sm text-red-700" role="alert">{apiErrorMessage(start.error, 'The analysis could not be started.')}</p>
        ) : null}
        {analyses.isLoading ? <p className="text-sm text-gray-500">Loading analyses…</p> : null}
        {analyses.isError ? (
          <p className="text-sm text-red-700" role="alert">Analyses could not be loaded.</p>
        ) : null}
        {analyses.data && analyses.data.data.length === 0 ? (
          <p className="text-sm text-gray-500">No statement analyses for this lead.</p>
        ) : null}
        <ul className="divide-y divide-gray-100">
          {analyses.data?.data.map((analysis) => {
            const selected = analysis.id === selectedId
            return (
              <li key={analysis.id} className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <Link to={`/statement-analysis/${analysis.id}`} className="font-medium text-sky-700 hover:text-sky-800">
                      {analysis.merchant_label}
                    </Link>
                    {selected ? (
                      <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                        Selected
                      </span>
                    ) : null}
                    <p className="text-xs text-gray-500">
                      {pricingModelLabel(analysis.pricing_model)}
                      {' · '}
                      {analysis.statement_period || 'No statement period'}
                    </p>
                    <p className="mt-1 text-sm text-gray-700">
                      Savings {formatCurrencyWithCentsOrDash(analysis.result?.merchant_savings)}
                      {' · '}
                      Margin {formatCurrencyWithCentsOrDash(analysis.result?.agms_margin)}
                    </p>
                    <p className="text-sm text-gray-700">
                      Proposed rates: {describeProposedRates(analysis.pricing_model, analysis.proposed_rates)}
                    </p>
                  </div>
                  {canUpdateLead && !selected ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => select.mutate(analysis.id)}
                      disabled={select.isPending}
                    >
                      Use for application
                    </Button>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
        {select.isError ? (
          <p className="text-sm text-red-700" role="alert">{apiErrorMessage(select.error, 'The analysis could not be selected.')}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}
